/**
 * TypingIndicator Component - Indicador de digitacao animado
 * @module features/chat/components/TypingIndicator
 *
 * Componente que exibe 3 pontos animados indicando que o assistente
 * esta "digitando". Totalmente acessivel com suporte a reduced motion.
 *
 * T002-FE: Interface de Chat Conversacional
 */

import type { TypingIndicatorProps } from '../types/chat.types';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Indicador visual de que o assistente esta digitando
 *
 * Features:
 * - 3 pontos com animacao bounce escalonada
 * - Suporte a prefers-reduced-motion
 * - aria-live para anuncio em screen readers
 * - Estilizacao consistente com design system
 *
 * @example
 * ```tsx
 * <TypingIndicator isVisible={isLoading} />
 * ```
 */
export function TypingIndicator({ isVisible }: TypingIndicatorProps) {
  if (!isVisible) {
    return null;
  }

  return (
    <div data-testid="typing-indicator-wrapper" className="flex justify-start">
      <div
        data-testid="typing-indicator"
        role="status"
        aria-live="polite"
        aria-label="Assistente digitando"
        className="motion-reduce:animate-none"
      >
        <div
          data-testid="typing-indicator-container"
          className="bg-muted rounded-lg px-4 py-3 inline-flex items-center gap-1"
        >
          {/* Screen reader text */}
          <span className="sr-only">Assistente digitando...</span>

          {/* Animated dots */}
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              data-testid="typing-dot"
              className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce motion-reduce:animate-none"
              style={{ animationDelay: `${index * 150}ms` }}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default TypingIndicator;
